import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { blogData } from '../data/blogData';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

export default function BlogList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const blogsPerPage = 6;

  const categories = ['All', ...new Set(blogData.map((blog) => blog.category))];

  const filteredBlogs = blogData.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.shortDescription.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Pagination Logic
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * blogsPerPage,
    currentPage * blogsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  return (
    <Box sx={{ py: 8, backgroundColor: '#f8f9fa', pt: { xs: 10, md: 12 } }}>
      <Helmet>
        <title>Blog | SLGT Digital Marketing Agency</title>
        <meta
          name="description"
          content="Insights, guides, and strategies on SEO, social media marketing, and digital growth from the SLGT team in Anakapalle & Visakhapatnam."
        />
        <link rel="canonical" href="https://www.slgt.in/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Blog | SLGT Digital Marketing Agency" />
        <meta
          property="og:description"
          content="Insights, guides, and strategies on SEO, social media marketing, and digital growth from the SLGT team."
        />
        <meta property="og:url" content="https://www.slgt.in/blog" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "SLGT Digital Marketing Blog",
            "url": "https://www.slgt.in/blog",
            "blogPost": blogData.map((blog) => ({
              "@type": "BlogPosting",
              "headline": blog.title,
              "url": `https://www.slgt.in/blog/${blog.slug}`,
              "datePublished": new Date(blog.date).toISOString().slice(0, 10),
              "author": { "@type": "Person", "name": blog.author },
            })),
          })}
        </script>
      </Helmet>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Nunito, serif',
              fontWeight: 700,
              color: '#1a1a1a',
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Our Blog
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Nunito, serif',
              color: '#666',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            Insights and updates from our marketing experts
          </Typography>
        </Box>

        {/* Search and Filter */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e0e0e0' },
                    '&:hover fieldset': { borderColor: '#FD8112' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    clickable
                    onClick={() => {
                      setSelectedCategory(category);
                      setCurrentPage(1);
                    }}
                    sx={{
                      backgroundColor: selectedCategory === category ? '#FD8112' : '#fff',
                      color: selectedCategory === category ? '#fff' : '#333',
                      border: selectedCategory === category ? 'none' : '1px solid #ddd',
                      fontWeight: selectedCategory === category ? 600 : 400,
                      '&:hover': {
                        backgroundColor: selectedCategory === category ? '#e67610' : '#f0f0f0',
                      },
                    }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Blog Grid with Animation */}
        <Grid container spacing={4}>
          {paginatedBlogs.length > 0 ? (
            paginatedBlogs.map((blog, index) => (
              <Grid item xs={12} sm={6} md={4} key={blog.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                >
                  <Card
                    onClick={() => navigate(`/blog/${blog.slug}`)}
                    role="button"
                    aria-label={`Read more about ${blog.title}`}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={blog.image}
                      alt={blog.title}
                      loading="lazy"
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        gutterBottom
                        variant="h6"
                        sx={{
                          fontFamily: 'Nunito, serif',
                          fontWeight: 600,
                          mb: 1,
                          lineHeight: 1.3,
                        }}
                      >
                        {blog.title}
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Chip
                          label={blog.category}
                          size="small"
                          sx={{
                            backgroundColor: '#FD8112',
                            color: '#fff',
                            mr: 1,
                            fontSize: '0.75rem',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: '#666', fontSize: '0.75rem' }}
                        >
                          {blog.date}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, fontFamily: 'Nunito, serif' }}
                      >
                        {blog.shortDescription}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#FD8112',
                          fontWeight: 600,
                          mt: 'auto',
                          fontSize: '0.9rem',
                        }}
                      >
                        Read More →
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{ textAlign: 'center', color: '#888', mt: 4 }}
              >
                No blogs found matching your search.
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Container>
    </Box>
  );
}
